import React, { useState } from 'react';
import './ChangePassword.css';

const ChangePassword = () => {
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Password
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [timer, setTimer] = useState(0);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailRegex.test(email);
  };

  // Send OTP
  const handleSendOtp = async () => {
    if (!validateEmail(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    try {
      // API call to send OTP would go here
      console.log('Sending OTP to:', formData.email);
      setIsOtpSent(true);
      setStep(2);
      startTimer();
    } catch (error) {
      setErrors({ email: 'Failed to send OTP. Please try again.' });
    }
  };

  // Start resend timer
  const startTimer = () => {
    setTimer(30);
    const interval = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Resend OTP
  const handleResendOtp = () => {
    console.log('Resending OTP to:', formData.email);
    startTimer();
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!formData.otp || formData.otp.length !== 6) {
      setErrors({ otp: 'Please enter a valid 6-digit OTP' });
      return;
    }

    try {
      // API call to verify OTP would go here
      console.log('Verifying OTP:', formData.otp);
      setIsOtpVerified(true);
      setStep(3);
    } catch (error) {
      setErrors({ otp: 'Invalid OTP. Please try again.' });
    }
  };

  // Validate password
  const validatePassword = (password) => {
    return password.length >= 8;
  };

  // Change password
  const handleChangePassword = async () => {
    const newErrors = {};

    if (!validatePassword(formData.newPassword)) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // API call to change password would go here
      console.log('Changing password for email:', formData.email);
      alert('Password changed successfully!');
      // Redirect to login or dashboard
    } catch (error) {
      setErrors({ newPassword: 'Failed to change password. Please try again.' });
    }
  };

  return (
    <div className="cp-container">
      <div className="cp-wrapper">
        <div className="cp-card">
          <div className="cp-header">
            <h2 className="cp-title">Change Password</h2>
            <div className="cp-steps">
              <div className={`cp-step ${step >= 1 ? 'cp-step-active' : ''}`}>
                <span className="cp-step-number">1</span>
                <span className="cp-step-text">Email</span>
              </div>
              <div className={`cp-step ${step >= 2 ? 'cp-step-active' : ''}`}>
                <span className="cp-step-number">2</span>
                <span className="cp-step-text">Verify</span>
              </div>
              <div className={`cp-step ${step >= 3 ? 'cp-step-active' : ''}`}>
                <span className="cp-step-number">3</span>
                <span className="cp-step-text">Password</span>
              </div>
            </div>
          </div>

          <div className="cp-content">
            {/* Step 1: Email */}
            {step === 1 && (
              <div className="cp-step-content">
                <div className="cp-step-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <h3 className="cp-step-title">Enter Your Email</h3>
                <p className="cp-step-description">
                  We'll send you an OTP to verify your identity
                </p>
                
                <div className="cp-form-group">
                  <label className="cp-label">Email Address</label>
                  <div className="cp-input-wrapper">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email address"
                      className={`cp-input ${errors.email ? 'cp-input-error' : ''}`}
                    />
                  </div>
                  {errors.email && <span className="cp-error">{errors.email}</span>}
                </div>

                <button
                  onClick={handleSendOtp}
                  disabled={!formData.email || !validateEmail(formData.email)}
                  className="cp-btn cp-btn-primary cp-btn-full"
                >
                  <i className="fas fa-paper-plane"></i>
                  Send OTP
                </button>
              </div>
            )}

            {/* Step 2: OTP Verification */}
            {step === 2 && (
              <div className="cp-step-content">
                <div className="cp-step-icon">
                  <i className="fas fa-shield-alt"></i>
                </div>
                <h3 className="cp-step-title">Verify OTP</h3>
                <p className="cp-step-description">
                  Enter the 6-digit OTP sent to {formData.email}
                </p>

                <div className="cp-form-group">
                  <label className="cp-label">Enter OTP</label>
                  <input
                    type="text"
                    name="otp"
                    value={formData.otp}
                    onChange={handleInputChange}
                    placeholder="Enter 6-digit OTP"
                    className={`cp-input cp-input-center ${errors.otp ? 'cp-input-error' : ''}`}
                    maxLength="6"
                  />
                  {errors.otp && <span className="cp-error">{errors.otp}</span>}
                </div>

                <div className="cp-otp-actions">
                  <button
                    onClick={handleVerifyOtp}
                    disabled={!formData.otp || formData.otp.length !== 6}
                    className="cp-btn cp-btn-primary"
                  >
                    <i className="fas fa-check"></i>
                    Verify OTP
                  </button>

                  <button
                    onClick={handleResendOtp}
                    disabled={timer > 0}
                    className="cp-btn cp-btn-secondary"
                  >
                    <i className="fas fa-redo"></i>
                    {timer > 0 ? `Resend in ${timer}s` : 'Resend OTP'}
                  </button>
                </div>

                <button
                  onClick={() => {
                    setStep(1);
                    setIsOtpSent(false);
                    setFormData(prev => ({ ...prev, otp: '' }));
                  }}
                  className="cp-btn cp-btn-text"
                >
                  <i className="fas fa-arrow-left"></i>
                  Change Email
                </button>
              </div>
            )}

            {/* Step 3: New Password */}
            {step === 3 && (
              <div className="cp-step-content">
                <div className="cp-step-icon">
                  <i className="fas fa-lock"></i>
                </div>
                <h3 className="cp-step-title">Create New Password</h3>
                <p className="cp-step-description">
                  Choose a strong password for your account
                </p>

                <div className="cp-form-group">
                  <label className="cp-label">New Password</label>
                  <div className="cp-password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      placeholder="Enter new password"
                      className={`cp-input cp-input-password ${errors.newPassword ? 'cp-input-error' : ''}`}
                    />
                    <button
                      type="button"
                      className="cp-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  {errors.newPassword && <span className="cp-error">{errors.newPassword}</span>}
                </div>

                <div className="cp-form-group">
                  <label className="cp-label">Confirm Password</label>
                  <div className="cp-password-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm new password"
                      className={`cp-input cp-input-password ${errors.confirmPassword ? 'cp-input-error' : ''}`}
                    />
                    <button
                      type="button"
                      className="cp-password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="cp-error">{errors.confirmPassword}</span>}
                </div>

                <div className="cp-password-requirements">
                  <p className="cp-requirements-title">Password must contain:</p>
                  <ul className="cp-requirements-list">
                    <li className={formData.newPassword.length >= 8 ? 'cp-requirement-met' : ''}>
                      <i className={`fas ${formData.newPassword.length >= 8 ? 'fa-check' : 'fa-times'}`}></i>
                      At least 8 characters
                    </li>
                  </ul>
                </div>

                <button
                  onClick={handleChangePassword}
                  disabled={
                    !formData.newPassword || 
                    !formData.confirmPassword || 
                    formData.newPassword !== formData.confirmPassword ||
                    formData.newPassword.length < 8
                  }
                  className="cp-btn cp-btn-success cp-btn-full"
                >
                  <i className="fas fa-save"></i>
                  Continue
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
